import { useState } from 'react';
import { ApiError, post, type Me } from './api';
import { PRIVACY_URL, SMS_TERMS, TERMS_URL } from './smsTerms';

/** The brand line used on sign-in screens, as in the prototype. */
export function Brand({ light = true }: { light?: boolean }) {
  return (
    <div className={'signbrand brand' + (light ? ' light' : '')} aria-label="MyWunderland Schedule">
      <span>My<span className="bw">Wunderland</span></span><span className="bdiv" aria-hidden="true" /><span className="bapp">Schedule</span>
    </div>
  );
}

type Step =
  | { k: 'phone' }
  | { k: 'pin'; firstName: string; pinLength: number }
  | { k: 'code'; firstName: string; by: 'text' | 'manager' }
  | { k: 'setpin'; setup: string; pinLength: number; again?: boolean }
  | { k: 'texts'; me: Me }
  | { k: 'note'; title: string; text: string };

/**
 * Mobile number → PIN (or, the first time / forgot PIN: one-time code → pick a PIN) → "Can we text you?".
 * Matches the prototype's phone sign-in (docs/sign-in.md).
 */
export function SignIn({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [step, setStep] = useState<Step>({ k: 'phone' });
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const digits = phone.replace(/\D/g, '');
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t reach MyWunderland. Check your connection.'); }
    finally { setBusy(false); }
  };
  const back = () => { setStep({ k: 'phone' }); setCode(''); setPin(''); setPin2(''); setErr(''); };

  const afterStart = (r: Record<string, unknown>) => {
    setCode(''); setPin(''); setPin2('');
    const first = String(r.firstName ?? '');
    if (r.step === 'pin') setStep({ k: 'pin', firstName: first, pinLength: Number(r.pinLength) });
    else if (r.step === 'code') setStep({ k: 'code', firstName: first, by: r.by === 'text' ? 'text' : 'manager' });
    else if (r.step === 'locked') setStep({ k: 'note', title: `Hi ${first}`, text: 'Too many wrong PINs. Ask your manager to reset your PIN on the Employees page, then sign in again.' });
    else if (r.step === 'off') setStep({ k: 'note', title: 'Sign-in is turned off', text: 'Your access has been turned off. Talk to your manager.' });
    else setStep({ k: 'note', title: 'We don’t have that number', text: 'Only mobile numbers on the Employees page can sign in. Ask your manager to add yours, or check you typed it right.' });
  };
  const signedIn = (r: { me: Me }) => (r.me.askTexts ? setStep({ k: 'texts', me: r.me }) : onSignedIn(r.me));

  return (
    <section className="signin">
      <Brand />
      {step.k === 'phone' && (
        <>
          <h1>Sign in</h1>
          <p>Type the mobile number your manager has on file.</p>
          <label>Mobile number
            <input className="inp" type="tel" inputMode="tel" autoComplete="tel" value={phone} placeholder="503-555-0100"
              aria-label="Mobile number" onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && digits.length >= 10) void run(async () => afterStart(await post('/api/auth/start', { phone }))); }} />
          </label>
          {err && <p className="signerr">{err}</p>}
          <button className="ebig" disabled={busy || digits.length < 10} onClick={() => run(async () => afterStart(await post('/api/auth/start', { phone })))}>Next</button>
        </>
      )}

      {step.k === 'pin' && (
        <>
          <h1>Welcome back, {step.firstName}</h1>
          <label>Your {step.pinLength}-digit PIN
            <input className="inp codeinp" type="password" inputMode="numeric" autoComplete="current-password" value={pin} maxLength={step.pinLength}
              placeholder={'•'.repeat(step.pinLength)} aria-label="Your PIN" autoFocus
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, step.pinLength))} />
          </label>
          {err && <p className="signerr">{err}</p>}
          <button className="ebig" disabled={busy || pin.length !== step.pinLength} onClick={() => run(async () => {
            const r = await post<Record<string, unknown>>('/api/auth/pin', { phone, pin });
            if (r.step === 'newpin') { setPin(''); setStep({ k: 'setpin', setup: String(r.setup), pinLength: Number(r.pinLength), again: true }); return; }
            signedIn(r as unknown as { me: Me });
          })}>Sign in</button>
          <button className="elink" onClick={() => run(async () => afterStart(await post('/api/auth/forgot', { phone })))}>Forgot PIN?</button>
          <button className="elink" onClick={back}>Use a different number</button>
        </>
      )}

      {step.k === 'code' && (
        <>
          <h1>Enter your code</h1>
          <p>{step.by === 'text'
            ? `We texted a 6-digit code to ${phone}. It works for 10 minutes.`
            : 'Ask a manager for a sign-in code. They’ll find it on the Employees page. It works for 15 minutes.'}</p>
          <label>Code
            <input className="inp codeinp" inputMode="numeric" autoComplete="one-time-code" value={code} maxLength={6} placeholder="••••••"
              aria-label="6-digit code" autoFocus onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
          </label>
          {err && <p className="signerr">{err}</p>}
          <button className="ebig" disabled={busy || code.length !== 6} onClick={() => run(async () => {
            const r = await post<{ setup: string; pinLength: number }>('/api/auth/code', { phone, code });
            setStep({ k: 'setpin', setup: r.setup, pinLength: r.pinLength });
          })}>Next</button>
          <button className="elink" onClick={back}>Use a different number</button>
        </>
      )}

      {step.k === 'setpin' && (
        <>
          <h1>Pick a {step.pinLength}-digit PIN</h1>
          <p>{step.again
            ? `Managers use a ${step.pinLength}-digit PIN. Pick a new one; your old PIN stops working.`
            : 'Next time you just type your number and this PIN. No text needed.'}</p>
          <label>New PIN
            <input className="inp codeinp" type="password" inputMode="numeric" autoComplete="new-password" value={pin} maxLength={step.pinLength}
              placeholder={'•'.repeat(step.pinLength)} aria-label="New PIN" autoFocus onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, step.pinLength))} />
          </label>
          <label>Type it again
            <input className="inp codeinp" type="password" inputMode="numeric" autoComplete="new-password" value={pin2} maxLength={step.pinLength}
              placeholder={'•'.repeat(step.pinLength)} aria-label="Confirm new PIN" onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, step.pinLength))} />
          </label>
          {err && <p className="signerr">{err}</p>}
          <button className="ebig" disabled={busy || pin.length !== step.pinLength || pin2.length !== step.pinLength} onClick={() => run(async () => {
            if (pin !== pin2) { setPin(''); setPin2(''); throw new ApiError(400, 'The two PINs didn’t match. Try again.', {}); }
            signedIn(await post<{ me: Me }>('/api/auth/pin/set', { setup: step.setup, pin }));
          })}>Save PIN and sign in</button>
        </>
      )}

      {step.k === 'texts' && (
        <>
          <h1>Can we text you?</h1>
          <p>Wunderland can send you automated texts with your schedules and shift updates, notices when a manager sends you a message, and sign-in codes if you forget your PIN.</p>
          <p>You don’t have to agree to use the app.</p>
          <div className="smsterms" role="region" aria-label="Text message program terms" tabIndex={0}>
            {SMS_TERMS.map((x) => <p key={x.h}><b>{x.h}:</b> {x.t}</p>)}
            <p><a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy policy</a> · <a href={TERMS_URL} target="_blank" rel="noopener">Terms &amp; conditions</a></p>
          </div>
          <label className="allday smsagree"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />I have read the terms above and agree to receive text messages from Wunderland at this number.</label>
          {err && <p className="signerr">{err}</p>}
          <button className="ebig" disabled={busy || !agree} onClick={() => run(async () => onSignedIn((await post<{ me: Me }>('/api/auth/texts', { yes: true, agreed: true })).me))}>Yes, text me</button>
          <button className="elink" onClick={() => run(async () => onSignedIn((await post<{ me: Me }>('/api/auth/texts', { yes: false })).me))}>No thanks, I’ll check the app</button>
        </>
      )}

      {step.k === 'note' && (
        <>
          <h1>{step.title}</h1>
          <p>{step.text}</p>
          <button className="ebig" onClick={back}>Back</button>
        </>
      )}
    </section>
  );
}
